import { isCapacitorIOS } from "@/lib/capacitor"

const SHRINK_THRESHOLD_PX = 72
const HIDE_DEBOUNCE_MS = 120
const IOS_HIDE_DEBOUNCE_MS = 180

let baselineInnerHeight = 0
let keyboardOpen = false
let hideDebounceId = 0

export function isNativeKeyboardOpen(): boolean {
  return keyboardOpen
}

export function forceNativeKeyboardOpen() {
  window.clearTimeout(hideDebounceId)
  hideDebounceId = 0
  keyboardOpen = true
}

export function forceNativeKeyboardClosed() {
  window.clearTimeout(hideDebounceId)
  hideDebounceId = 0
  keyboardOpen = false
  baselineInnerHeight = Math.max(baselineInnerHeight, window.innerHeight)
}

function updateBaselineWhenClosed() {
  const h = window.innerHeight
  if (!keyboardOpen) {
    baselineInnerHeight = Math.max(baselineInnerHeight, h)
  }
}

/** Android: layout viewport height dropped — fixed `bottom:0` already sits above the keyboard. */
export function getLayoutViewportShrinkPx(): number {
  updateBaselineWhenClosed()
  if (baselineInnerHeight === 0) return 0
  return Math.max(0, baselineInnerHeight - window.innerHeight)
}

export function isLayoutViewportShrunkForKeyboard(): boolean {
  return getLayoutViewportShrinkPx() >= SHRINK_THRESHOLD_PX
}

/** Occluded px at the bottom (overlay keyboard — WebView height unchanged). */
export function measureViewportKeyboardOcclusionPx(): number {
  const vv = window.visualViewport
  if (!vv) return 0
  return Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
}

function measureIOSKeyboardOpen(): boolean {
  return measureViewportKeyboardOcclusionPx() > SHRINK_THRESHOLD_PX
}

/** True when the WebView height dropped vs our stored full-screen baseline (Capacitor plugin events often missing on Android). */
export function measureNativeKeyboardOpen(): boolean {
  if (isCapacitorIOS()) {
    return measureIOSKeyboardOpen()
  }

  const innerH = window.innerHeight
  const vv = window.visualViewport
  const vvH = vv?.height ?? innerH

  updateBaselineWhenClosed()

  if (baselineInnerHeight === 0) {
    baselineInnerHeight = innerH
    return false
  }

  const innerShrunk = innerH < baselineInnerHeight - SHRINK_THRESHOLD_PX
  const vvGap = innerH - vvH > SHRINK_THRESHOLD_PX

  return innerShrunk || vvGap
}

export type KeyboardViewportCallbacks = {
  onShow: () => void
  onHide: () => void
}

export function attachNativeKeyboardViewportListener(callbacks: KeyboardViewportCallbacks): () => void {
  const hideDebounceMs = isCapacitorIOS() ? IOS_HIDE_DEBOUNCE_MS : HIDE_DEBOUNCE_MS

  const onViewportChange = () => {
    const nextOpen = measureNativeKeyboardOpen()

    window.clearTimeout(hideDebounceId)
    hideDebounceId = 0

    if (nextOpen) {
      keyboardOpen = true
      callbacks.onShow()
      return
    }

    if (keyboardOpen) {
      hideDebounceId = window.setTimeout(() => {
        hideDebounceId = 0
        if (measureNativeKeyboardOpen()) return
        keyboardOpen = false
        baselineInnerHeight = Math.max(baselineInnerHeight, window.innerHeight)
        callbacks.onHide()
      }, hideDebounceMs)
    }
  }

  const onResize = () => onViewportChange()
  const onScroll = () => onViewportChange()

  baselineInnerHeight = window.innerHeight
  window.visualViewport?.addEventListener("resize", onResize)
  window.visualViewport?.addEventListener("scroll", onScroll)
  window.addEventListener("resize", onResize)

  return () => {
    window.clearTimeout(hideDebounceId)
    window.visualViewport?.removeEventListener("resize", onResize)
    window.visualViewport?.removeEventListener("scroll", onScroll)
    window.removeEventListener("resize", onResize)
    keyboardOpen = false
    hideDebounceId = 0
  }
}
